'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { usePathname, useRouter } from 'next/navigation'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react'
import {
  Bars3Icon,
  HomeIcon,
  MapIcon,
  XMarkIcon,
  CameraIcon,
  BuildingOfficeIcon,
  AdjustmentsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  RectangleGroupIcon,
  ShieldCheckIcon,
  KeyIcon,
  BellAlertIcon,
  BoltIcon,
  DocumentDuplicateIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  PhotoIcon
} from '@heroicons/react/24/outline'
import Image from 'next/image'
import { UserButton, OrganizationSwitcher, OrganizationProfile, useOrganization } from '@clerk/nextjs'
import { StationsProvider, useStations } from '@/contexts/stations-context'
import { usePreferences } from '@/contexts/preferences-context'
import { DownloadProvider } from '@/contexts/download-context'
import AutoSelectOrganization from '@/components/auto-select-organization'
import OrgAuthSettings from '@/components/org-auth'
import UserPreferences, { PreferencesSetupDialog } from '@/components/user-preferences'
import Search from '@/components/search'
import { useIsSuperOrg } from '@/lib/auth'
import { useOrgStatus } from '@/components/org-guard'
import UserNotifications, { AlertPreferencesDialog } from '@/components/user-notifications'
import NotificationDropdown from '@/components/notification-dropdown'

const OrganizationSettings = dynamic(() => import('@/components/organization-settings'), {
  ssr: false,
  loading: () => <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading organization settings...</div>,
})

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  {
    name: 'HazCams',
    href: '/stations',
    icon: CameraIcon,
    children: [
      { name: 'Map', href: '/map', icon: MapIcon },
      { name: 'Monitor', href: '/monitor', icon: RectangleGroupIcon },
    ],
  },
  {
    name: 'HazAlerts',
    href: '/hazalerts',
    icon: BellAlertIcon
  },
  {
    name: 'Strike Zone',
    href: '/strike-zone',
    icon: BoltIcon
  }
]

const hazcamsRequiredRoutes = ['/map', '/monitor']

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

function isNavigationItemActive(pathname, item) {
  if (!pathname) return false
  if (item.href === '/stations' || item.href === '/hazalerts' || item.href === '/strike-zone') {
    return pathname.split('/').filter(Boolean)[0] === item.href.slice(1)
  }
  return pathname === item.href
}

function NavigationItem({ item, pathname, hasOrganization, collapsed = false, child = false, onNavigate }) {
  const active = isNavigationItemActive(pathname, item)
  const Icon = item.icon

  if (!hasOrganization) {
    return (
      <div
        className={classNames(
          'group flex gap-x-3 rounded-md text-sm/6 font-semibold text-gray-500 opacity-60 cursor-not-allowed',
          child && !collapsed ? 'pl-3' : '',
          collapsed ? 'mx-auto size-10 items-center justify-center p-0' : 'p-2',
        )}
        title={collapsed ? `${item.name} (Requires Organization)` : undefined}
      >
        <Icon aria-hidden="true" className={classNames('shrink-0 text-gray-500', child ? 'size-5' : 'size-6')} />
        {!collapsed && item.name}
      </div>
    )
  }

  return (
    <Link
      href={item.href}
      aria-current={active ? 'page' : undefined}
      onClick={onNavigate}
      className={classNames(
        active
          ? 'bg-gray-700 text-white dark:bg-white/10'
          : 'text-gray-300 hover:bg-gray-700 hover:text-white dark:text-gray-300 dark:hover:bg-white/10',
        'group flex gap-x-3 rounded-md text-sm/6 font-semibold',
        child && !collapsed ? 'pl-3' : '',
        collapsed ? 'mx-auto size-10 items-center justify-center p-0' : 'p-2',
      )}
      title={collapsed ? item.name : undefined}
    >
      <Icon
        aria-hidden="true"
        className={classNames(
          active ? 'text-white' : 'text-gray-300 group-hover:text-white dark:text-gray-300',
          child ? 'size-5' : 'size-6',
          'shrink-0',
        )}
      />
      {!collapsed && item.name}
    </Link>
  )
}

function HazcamsRouteGuard({ children }) {
  const pathname = usePathname()
  const router = useRouter()
  const { stations, loading, error } = useStations()
  const requiresHazcams = hazcamsRequiredRoutes.some(
    (route) => pathname === route || pathname?.startsWith(`${route}/`),
  )
  const shouldRedirect = requiresHazcams && !loading && !error && !stations?.length

  useEffect(() => {
    if (shouldRedirect) router.replace('/stations')
  }, [router, shouldRedirect])

  if (requiresHazcams && (loading || shouldRedirect)) {
    return (
      <div className="flex min-h-[calc(100dvh-var(--header-offset))] items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading HazCams...
      </div>
    )
  }

  return children
}

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [preferencesSetupOpen, setPreferencesSetupOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const { preferences, updatePreference, loading, needsSetup } = usePreferences()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(preferences.sidebarCollapsed ?? false)
  const pathname = usePathname()
  const { organization, membership } = useOrganization()
  const { hasOrganization } = useOrgStatus()
  const isUsingHazcamsModule = pathname === '/stations'
    || pathname?.startsWith('/stations/')
    || hazcamsRequiredRoutes.some((route) => pathname === route || pathname?.startsWith(`${route}/`))

  // Check if current org is super org
  const isAdmin = useIsSuperOrg(organization)
  // Build navigation items dynamically based on permissions
  const navigationItems = [
    ...navigation,
    ...(isAdmin ? [{ name: 'Administration', href: '/administration', icon: ShieldCheckIcon }] : [])
  ]

  // Load sidebar collapsed state from preferences
  useEffect(() => {
    if (!loading && preferences.sidebarCollapsed !== undefined) {
      setSidebarCollapsed(preferences.sidebarCollapsed)
    }
  }, [loading, preferences.sidebarCollapsed])

  useEffect(() => {
    const openPreferencesSetup = () => setPreferencesSetupOpen(true)
    window.addEventListener('open-hazcams-preferences-setup', openPreferencesSetup)
    return () => window.removeEventListener('open-hazcams-preferences-setup', openPreferencesSetup)
  }, [])

  useEffect(() => {
    const fullscreenDisplayMode = window.matchMedia('(display-mode: fullscreen)')
    const updateFullscreenState = () => {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement
      const fullscreen = Boolean(fullscreenElement || fullscreenDisplayMode.matches)

      setIsFullscreen(fullscreen)
      if (fullscreen) setSidebarOpen(false)
    }

    updateFullscreenState()
    document.addEventListener('fullscreenchange', updateFullscreenState)
    document.addEventListener('webkitfullscreenchange', updateFullscreenState)
    fullscreenDisplayMode.addEventListener('change', updateFullscreenState)

    return () => {
      document.removeEventListener('fullscreenchange', updateFullscreenState)
      document.removeEventListener('webkitfullscreenchange', updateFullscreenState)
      fullscreenDisplayMode.removeEventListener('change', updateFullscreenState)
    }
  }, [])

  // Save sidebar collapsed state to preferences when it changes
  const toggleSidebarCollapsed = () => {
    const newCollapsedState = !sidebarCollapsed
    setSidebarCollapsed(newCollapsedState)
    updatePreference('sidebarCollapsed', newCollapsedState)
  }

  // Don't show layout on root page (which redirects)
  if (pathname === '/') {
    return <div className="min-h-screen bg-gray-50">{children}</div>
  }

  return (
    <DownloadProvider>
      <StationsProvider>
        <AutoSelectOrganization />
        <AlertPreferencesDialog
          open={!loading && !preferences.notificationSetupCompleted}
        />
        <PreferencesSetupDialog
          open={!loading && preferences.notificationSetupCompleted && ((needsSetup && isUsingHazcamsModule) || preferencesSetupOpen)}
          firstRun={needsSetup}
          onClose={() => setPreferencesSetupOpen(false)}
        />
        <div style={{ '--header-offset': isFullscreen ? '0rem' : '4rem' }}>
        {!isFullscreen && <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-closed:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                  </button>
                </div>
              </TransitionChild>

              {/* Mobile Sidebar */}
              <div className="relative flex grow flex-col gap-y-5 overflow-y-auto bg-gray-800 px-6 pb-4 dark:bg-gray-950 dark:ring-1 dark:ring-white/10">
                <div className="flex h-16 shrink-0 items-center">
                  <Image
                    src="/weather-logic-light.svg"
                    alt="WXLOGIC"
                    width={168}
                    height={30}
                    priority
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigationItems.map((item) => (
                          <li key={item.name}>
                            <NavigationItem
                              item={item}
                              pathname={pathname}
                              hasOrganization={hasOrganization}
                              onNavigate={() => setSidebarOpen(false)}
                            />
                            {item.children?.length ? (
                              <ul className="ml-5 mt-1 space-y-1 border-l border-gray-700 pl-3 dark:border-white/10">
                                {item.children.map((child) => (
                                  <li key={child.name}>
                                    <NavigationItem
                                      item={child}
                                      pathname={pathname}
                                      hasOrganization={hasOrganization}
                                      child
                                      onNavigate={() => setSidebarOpen(false)}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </li>
              
                    <li className="mt-auto">
                      <OrganizationSwitcher
                        hidePersonal={true}
                        createOrganizationMode="navigation"
                        createOrganizationUrl="#"
                        appearance={{
                          elements: {
                            organizationSwitcherTrigger: '!bg-white !w-full !p-2 !align-middle',
                            organizationSwitcherPopoverActionButton__createOrganization: '!hidden',
                          }
                        }}
                      >
                        <OrganizationProfile.Page
                          label="API"
                          labelIcon={<DocumentDuplicateIcon className="w-5 h-5" />}
                          url="api"
                        >
                          <OrganizationSettings initialTab="api" showNavigation={false} embedded />
                        </OrganizationProfile.Page>
                        <OrganizationProfile.Page
                          label="Public page"
                          labelIcon={<GlobeAltIcon className="w-5 h-5" />}
                          url="public-page"
                        >
                          <OrganizationSettings initialTab="publicPage" showNavigation={false} embedded />
                        </OrganizationProfile.Page>
                        <OrganizationProfile.Page
                          label="Branding"
                          labelIcon={<PaintBrushIcon className="w-5 h-5" />}
                          url="branding"
                        >
                          <OrganizationSettings initialTab="branding" showNavigation={false} embedded />
                        </OrganizationProfile.Page>
                        <OrganizationProfile.Page
                          label="Overlay graphics"
                          labelIcon={<PhotoIcon className="w-5 h-5" />}
                          url="overlay-graphics"
                        >
                          <OrganizationSettings initialTab="overlay" showNavigation={false} embedded />
                        </OrganizationProfile.Page>
                        {membership?.role === 'org:admin' && (
                          <OrganizationProfile.Page
                            label="API key"
                            labelIcon={<KeyIcon className="w-5 h-5" />}
                            url="auth"
                          >
                            <OrgAuthSettings />
                          </OrganizationProfile.Page>
                        )}
                      </OrganizationSwitcher>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>}

        {/* Static sidebar for desktop */}
        {!isFullscreen && <div className={classNames(
          "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300",
          sidebarCollapsed ? "lg:w-20" : "lg:w-72"
        )}>
          <div className="relative flex grow flex-col gap-y-5 overflow-y-auto overflow-x-hidden bg-gray-800 px-6 pb-4 dark:bg-gray-950 dark:after:pointer-events-none dark:after:absolute dark:after:inset-y-0 dark:after:right-0 dark:after:w-px dark:after:bg-white/10">
            <div className="flex h-16 shrink-0 items-center justify-between">
              {sidebarCollapsed ? (
                <Link href="/dashboard" className="mx-auto rounded-lg ">
                  <Image
                    src="/wxlogic_white_square_icon_1024_transparent.png"
                    alt="WXLOGIC"
                    width={42}
                    height={42}
                    priority
                  />
                </Link>
              ) : (
                <Link href="/dashboard">
                  <Image
                    src="/weather-logic-light.svg"
                    alt="WXLOGIC"
                    width={168}
                    height={30}
                    priority
                  />
                </Link>
              )}
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigationItems.map((item) => (
                      <li key={item.name}>
                        <NavigationItem
                          item={item}
                          pathname={pathname}
                          hasOrganization={hasOrganization}
                          collapsed={sidebarCollapsed}
                        />
                        {item.children?.length ? (
                          <ul className={classNames(
                            'mt-1 space-y-1',
                            sidebarCollapsed ? '' : 'ml-5 border-l border-gray-700 pl-3 dark:border-white/10',
                          )}>
                            {item.children.map((child) => (
                              <li key={child.name}>
                                <NavigationItem
                                  item={child}
                                  pathname={pathname}
                                  hasOrganization={hasOrganization}
                                  collapsed={sidebarCollapsed}
                                  child
                                />
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto flex justify-center">
                  {sidebarCollapsed ? (
                    <div className="w-10 h-10 rounded-md overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                      {organization?.imageUrl ? (
                        <Image 
                          src={organization.imageUrl} 
                          alt={organization.name || 'Organization'}
                          width={40}
                          height={40}
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <BuildingOfficeIcon className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                  ) : (
                    <OrganizationSwitcher 
                      hidePersonal={true}
                      createOrganizationMode="navigation"
                      createOrganizationUrl="#"
                      appearance={{
                        elements: {
                          organizationSwitcherTrigger: '!bg-white !w-full !p-2 !align-middle',
                          organizationSwitcherPopoverActionButton__createOrganization: '!hidden',
                        }
                      }}
                    >
                      <OrganizationProfile.Page
                        label="API"
                        labelIcon={<DocumentDuplicateIcon className="w-5 h-5" />}
                        url="api"
                      >
                        <OrganizationSettings initialTab="api" showNavigation={false} embedded />
                      </OrganizationProfile.Page>
                      <OrganizationProfile.Page
                        label="Public page"
                        labelIcon={<GlobeAltIcon className="w-5 h-5" />}
                        url="public-page"
                      >
                        <OrganizationSettings initialTab="publicPage" showNavigation={false} embedded />
                      </OrganizationProfile.Page>
                      <OrganizationProfile.Page
                        label="Branding"
                        labelIcon={<PaintBrushIcon className="w-5 h-5" />}
                        url="branding"
                      >
                        <OrganizationSettings initialTab="branding" showNavigation={false} embedded />
                      </OrganizationProfile.Page>
                      <OrganizationProfile.Page
                        label="Overlay graphics"
                        labelIcon={<PhotoIcon className="w-5 h-5" />}
                        url="overlay-graphics"
                      >
                        <OrganizationSettings initialTab="overlay" showNavigation={false} embedded />
                      </OrganizationProfile.Page>
                      {membership?.role === 'org:admin' && (
                        <OrganizationProfile.Page 
                          label="API key"
                          labelIcon={<KeyIcon className="w-5 h-5" />}
                          url="auth"
                        >
                          <OrgAuthSettings />
                        </OrganizationProfile.Page>
                      )}
                    </OrganizationSwitcher>
                  )}
                </li>
              </ul>
            </nav>
            {/* Collapse toggle button */}
            <button
              onClick={toggleSidebarCollapsed}
              className="absolute right-0 bottom-20 z-10 flex h-12 w-6 items-center justify-center rounded-l-lg bg-gray-700 transition-all hover:bg-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700"
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-5 w-5 text-white" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
        </div>}

        <div className={classNames(
          "transition-all duration-300",
          !isFullscreen && (sidebarCollapsed ? "lg:pl-20" : "lg:pl-72")
        )}>
          {!isFullscreen && <div className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-gray-100 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 dark:border-gray-700 dark:bg-gray-900">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="-m-2.5 p-2.5 text-foreground-muted hover:text-foreground lg:hidden"
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            {/* Separator */}
            <div aria-hidden="true" className="h-6 w-px bg-gray-900/10 lg:hidden dark:bg-white/10" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <Search />
              <div className="flex items-center gap-x-4 lg:gap-x-6 h-full">
                <NotificationDropdown />
                

                {/* Separator */}
                <div
                  aria-hidden="true"
                  className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10 dark:lg:bg-gray-100/10"
                />

                {/* Profile dropdown */}
                <div className="flex items-center justify-center h-16">
                  <UserButton
                    showName={true}
                    appearance={{
                      elements: {
                        userButtonBox: 'text-gray-400 dark:text-gray-100 flex items-center justify-center align-middle ',
                      }
                    }}
                  >
                    <UserButton.UserProfilePage 
                      label="Notifications" 
                      labelIcon={<BellAlertIcon className="w-5 h-5" />}
                      url="notifications"
                    >
                      <UserNotifications />
                    </UserButton.UserProfilePage>
                    <UserButton.UserProfilePage 
                      label="Preferences" 
                      labelIcon={<AdjustmentsHorizontalIcon className="w-5 h-5" />}
                      url="preferences"
                    >
                      <UserPreferences />
                    </UserButton.UserProfilePage>
                  </UserButton>
                </div>
              </div>
            </div>
          </div>}

          <main>
            <HazcamsRouteGuard>{children}</HazcamsRouteGuard>
          </main>
        </div>
      </div>
    </StationsProvider>
    </DownloadProvider>
  )
}

